import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Customer } from '../../models/customer.model';
import { FacilityType } from '../../models/facility-type.model';
import { Permission } from '../../models/permission.model';
import { AccountService } from '../../services/account.service';
import { AlertService, MessageSeverity } from '../../services/alert.service';
import { fadeInOut } from '../../services/animations';
import { CustomersGroupService } from '../../services/customers-group.service';
import { CustomersService } from '../../services/customers.service';
import { FacilityTypeService } from '../../services/facility-type.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'customer-group',
    template: require('./customers-group.component.html'),
    styles: [require('./customers-group.component.css')],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.None
})
export class CustomersGroupComponent implements OnInit, OnDestroy {
    private subscribers: Subscription[] = [];


    customer: Customer = new Customer;
    sourceCustomers: Customer[] = [];
    targetCustomers: Customer[] = [];
    facilityTypes: FacilityType[] = [];
    isGroupShow: boolean = false;
    mode: string = "Toevoegen";
    isElectricityContractManage: boolean = false;
    isInjectionContractManage: boolean = false;
    isGASContractManage: boolean = false;

    constructor(private facilityTypeService: FacilityTypeService, private customersService: CustomersService, private customersGroupService: CustomersGroupService,
        private router: Router, private activatedRoute: ActivatedRoute, private alertService: AlertService, private ngxLogger: NGXLogger, private accountService: AccountService) { }

    ngOnInit(): void {
        this.customer.CustomerType = "Group";

        const fcGetSubscriber = this.facilityTypeService.get().subscribe((res: FacilityType[]) => {
            this.facilityTypes = res;
        });

        this.subscribers.push(fcGetSubscriber);

        const id = this.activatedRoute.snapshot.params["id"];
        if (id) {
            this.mode = "Bewerk";
            this.customer.Id = id;
            this.isGroupShow = true;
            this.getById();
        } else {
            this.mode = "Toevoegen";
            this.isGroupShow = false;
            this.customer.Id = 0;
        }

        this.getSourceCustomers();
        this.getTargetCustomers();

        if (this.canelectricityContractAdd || this.canelectricityContractDelete || this.canelectricityContractEdit || this.canelectricityContractList || this.canelectricityContractView) {
            this.isElectricityContractManage = true;
        }

        if (this.caninjectionContractAdd || this.caninjectionContractDelete || this.caninjectionContractEdit || this.caninjectionContractList) {
            this.isInjectionContractManage = true;
        }

        if (this.canGASContractAdd || this.canGASContractDelete || this.canGASContractEdit || this.canGASContractList || this.canGASContractView) {
            this.isGASContractManage = true;
        }
    }

    getById() {
        const getByIdSubscriber =  this.customersService.getById(this.customer.Id).subscribe((res: Customer) => {
            this.customer = res;
        }, error => {
            this.ngxLogger.error(error.message);
            throw error;
            });
        this.subscribers.push(getByIdSubscriber);
    }

    getSourceCustomers() {
        if (this.mode == "Bewerk") {
           const getSRcCustSubscriber =  this.customersGroupService.getSourceCustomers(this.customer.Id).subscribe((res: Customer[]) => {
                this.sourceCustomers = res.filter(c => c.IsActive == true);
            }, error => {
                this.ngxLogger.error(error.message);
                throw error;
                });

            this.subscribers.push(getSRcCustSubscriber);
        }
    }

    getSourceCustomersByFacility(facilityTypeId: number) {
        if (this.mode == "Toevoegen") {
           const getSRCCustFacilitySubscriber =  this.customersGroupService.getSourceCustomersForFacility(facilityTypeId).subscribe((res: Customer[]) => {
                this.sourceCustomers = res.filter(c => c.IsActive == true);
                this.isGroupShow = true;
            }, error => {
                this.ngxLogger.error(error.message);
                throw error;
                });

            this.subscribers.push(getSRCCustFacilitySubscriber);
        }
    }

    getTargetCustomers() {
        if (this.mode == "Bewerk") {
           const getTRGCustSubscriber =  this.customersGroupService.getTargetCustomers(this.customer.Id).subscribe((res: Customer[]) => {
                this.targetCustomers = res;
            }, error => {
                this.ngxLogger.error(error.message);
                throw error;
                });

            this.subscribers.push(getTRGCustSubscriber);
        }
    }

    showErrorAlert(caption: string, message: string) {
        this.alertService.showMessage(caption, message, MessageSeverity.error);
    }

    updateCustomerGroup() {
        if (this.mode == "Toevoegen") {
            this.customer.Id = 0;
        }

        if (this.targetCustomers.length > 0) {
           const updCusGrpSubscriber =  this.customersGroupService.updateCustomerGroup(this.customer.Id, this.customer.GroupName, this.customer.FacilityTypeId, this.targetCustomers).subscribe((res: Customer[]) => {
                this.alertService.showMessage('Klantgroep', 'Klantgroep succesvol opgeslagen!', MessageSeverity.success);
                if (this.mode == "Toevoegen") {
                    this.router.navigate(['customer/group/', res]);
               }
               this.subscribers.push(updCusGrpSubscriber);

            }, error => {
                this.ngxLogger.error(error.message);
                throw error;
            });
        } else {
            this.alertService.showMessage('Klantgroep', 'Selecteer ten minste één klant!', MessageSeverity.error);
        }
    }

    ngOnDestroy() {
        if (this.subscribers) {
            this.subscribers.forEach(s => s.unsubscribe());
        }
    }

    get canelectricityContractAdd() {
        return this.accountService.userHasPermission(Permission.electricityContractAddPermission);
    }

    get canelectricityContractEdit() {
        return this.accountService.userHasPermission(Permission.electricityContractEditPermission);
    }

    get canelectricityContractDelete() {
        return this.accountService.userHasPermission(Permission.electricityContractDeletePermission);
    }

    get canelectricityContractView() {
        return this.accountService.userHasPermission(Permission.electricityContractViewPermission);
    }

    get canelectricityContractList() {
        return this.accountService.userHasPermission(Permission.electricityContractListPermission);
    }

    get caninjectionContractAdd() {
        return this.accountService.userHasPermission(Permission.injectionContractAddPermission);
    }

    get caninjectionContractEdit() {
        return this.accountService.userHasPermission(Permission.injectionContractEditPermission);
    }

    get caninjectionContractDelete() {
        return this.accountService.userHasPermission(Permission.injectionContractDeletePermission);
    }

    get caninjectionContractView() {
        return this.accountService.userHasPermission(Permission.injectionContractViewPermission);
    }

    get caninjectionContractList() {
        return this.accountService.userHasPermission(Permission.injectionContractListPermission);
    }

    get canGASContractAdd() {
        return this.accountService.userHasPermission(Permission.gasContractAddPermission);
    }

    get canGASContractEdit() {
        return this.accountService.userHasPermission(Permission.gasContractEditPermission);
    }

    get canGASContractDelete() {
        return this.accountService.userHasPermission(Permission.gasContractDeletePermission);
    }

    get canGASContractView() {
        return this.accountService.userHasPermission(Permission.gasContractViewPermission);
    }

    get canGASContractList() {
        return this.accountService.userHasPermission(Permission.gasContractListPermission);
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/customers/customers-group.component.ts